// Stats compute helpers — pure functions over the raw event arrays.
//
// Sleep metrics use cluster-based "awake-day" attribution from sleep-stats.ts.
// Each sleep session belongs to exactly one calendar day:
//   - Night cluster → day the cluster ended on (so a 19:00→07:00 night sleep
//     counts toward the morning that bookends it, not split across two days).
//   - Nap (day cluster) → day of the midpoint.
//
// This keeps the Stats page consistent with the Home tile and prevents the
// midnight-cutoff distortion where today's bucket would only see the
// post-midnight slice of last night.

import type {
  DiaperEvent,
  FeedingSession,
  PumpingSession,
  SleepSession,
} from '@shared/types.ts';
import { clusterSleepSessions, type SleepCluster } from './sleep-stats.ts';

const DAY = 86400;

export function startOfDayLocal(d: Date): Date {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
}

export function startOfDaySec(d: Date): number {
  return Math.floor(startOfDayLocal(d).getTime() / 1000);
}

function startOfDaySecFromEpoch(sec: number): number {
  const d = new Date(sec * 1000);
  d.setHours(0, 0, 0, 0);
  return Math.floor(d.getTime() / 1000);
}

export type DayStats = {
  /** Last night + naps + tonight-so-far attributed to this day. */
  sleepTotalMin: number;
  /** Night cluster that ended (closed) on this day. Excludes any night
   *  session currently in progress — that goes into tonightSoFarMin. */
  lastNightMin: number;
  /** Sum of in-progress night clusters attributed to this day. Non-zero
   *  only on the current day while a night sleep is live. */
  tonightSoFarMin: number;
  /** Sum of nap clusters attributed to this day. */
  napsMin: number;
  /** Longest single session attributed to this day. */
  longestStretchMin: number;
  /** Number of nap clusters attributed to this day. */
  naps: number;
  /** Awake-window minutes, chronological: night-end → nap1-start →
   *  nap1-end → nap2-start → ... → last-nap-end → next-bedtime-start. */
  wakeWindows: number[];
  feedCount: number;
  feedsByType: { bottle: number; breast: number; solid: number };
  bottleMl: number;
  pumpCount: number;
  pumpYieldMl: number;
  diaperCount: number;
  diapersByKind: { pee: number; poop: number; both: number };
};

export function computeDayStats({
  sleep,
  feed,
  pump,
  diaper,
  date,
  now,
}: {
  sleep: SleepSession[];
  feed: FeedingSession[];
  pump: PumpingSession[];
  diaper: DiaperEvent[];
  date: Date;
  now?: Date;
}): DayStats {
  const from = startOfDaySec(date);
  const to = from + DAY;
  const clusters = clusterSleepSessions(sleep, now);

  const owned = clusters.filter((c) => c.attributedDayStartSec === from);

  let lastNightMin = 0;
  let tonightSoFarMin = 0;
  let napsMin = 0;
  let longestStretchMin = 0;
  let napsCount = 0;
  for (const c of owned) {
    if (c.kind === 'night') {
      // A night cluster owned by this day plays one of two roles:
      //   - "last night": the morning wake (started before this day, or
      //     before 14:00 today). Bookends today's awake stretch.
      //   - "tonight":    the evening bedtime that STARTED today after
      //     14:00. It may have already ended (baby woke up) — what
      //     matters is the start day, matching Apple Health semantics.
      const first = c.sessions[0]!;
      const startedToday = startOfDaySecFromEpoch(first.started_at) === from;
      const startHour = new Date(first.started_at * 1000).getHours();
      const isTonight = startedToday && startHour >= 14;
      if (isTonight) tonightSoFarMin += c.totalMinutes;
      else lastNightMin += c.totalMinutes;
    } else {
      napsMin += c.totalMinutes;
      napsCount += 1;
    }
    for (const s of c.sessions) {
      const end = s.ended_at ?? Math.floor((now ?? new Date()).getTime() / 1000);
      const mins = Math.max(0, Math.floor((end - s.started_at) / 60));
      if (mins > longestStretchMin) longestStretchMin = mins;
    }
  }

  // Wake windows: ordered awake-time chunks in this day's awake window.
  // Starts from the night-cluster end and walks through this day's clusters
  // up to (but not including) the next night cluster.
  const wakeWindows = computeWakeWindowsForDay(clusters, from);

  const inDay = feed.filter((f) => f.started_at >= from && f.started_at < to);
  const feedsByType = {
    bottle: inDay.filter((f) => f.type === 'bottle').length,
    breast: inDay.filter((f) => f.type === 'breast_left' || f.type === 'breast_right').length,
    solid: inDay.filter((f) => f.type === 'solid').length,
  };
  const bottleMl = inDay
    .filter((f) => f.type === 'bottle')
    .reduce((a, f) => a + (f.amount_ml ?? 0), 0);

  const pumpSessions = pump.filter((p) => p.started_at >= from && p.started_at < to);
  const pumpCount = pumpSessions.length;
  const pumpYieldMl = pumpSessions.reduce((a, p) => a + (p.yield_ml ?? 0), 0);

  const inDiapers = diaper.filter((d) => d.occurred_at >= from && d.occurred_at < to);
  const diapersByKind = {
    pee: inDiapers.filter((d) => d.kind === 'pee').length,
    poop: inDiapers.filter((d) => d.kind === 'poop').length,
    both: inDiapers.filter((d) => d.kind === 'both').length,
  };

  return {
    sleepTotalMin: lastNightMin + napsMin + tonightSoFarMin,
    lastNightMin,
    tonightSoFarMin,
    napsMin,
    longestStretchMin,
    naps: napsCount,
    wakeWindows,
    feedCount: inDay.length,
    feedsByType,
    bottleMl,
    pumpCount,
    pumpYieldMl,
    diaperCount: inDiapers.length,
    diapersByKind,
  };
}

function computeWakeWindowsForDay(clusters: SleepCluster[], dayStartSec: number): number[] {
  // Find the night cluster that ENDED this day (the morning wake), then walk
  // forward through this day's nap clusters and the next bedtime. Each gap
  // between consecutive sleeps is a wake window.
  const dayEnd = dayStartSec + DAY;
  const sorted = [...clusters].sort((a, b) => a.sessions[0]!.started_at - b.sessions[0]!.started_at);

  const nightCluster = sorted.find(
    (c) => c.kind === 'night' && c.attributedDayStartSec === dayStartSec,
  );
  const napClusters = sorted.filter(
    (c) => c.kind === 'day' && c.attributedDayStartSec === dayStartSec,
  );
  // First cluster after this day's naps, regardless of kind — the next
  // bedtime caps the final wake window.
  const lastNapEnd = napClusters.length ? napClusters[napClusters.length - 1]!.endSec : null;
  const nextCluster = sorted.find((c) => {
    const start = c.sessions[0]!.started_at;
    return start >= (lastNapEnd ?? dayStartSec) && start < dayEnd + DAY;
  });

  const events: { start: number; end: number }[] = [];
  if (nightCluster) events.push({ start: nightCluster.endSec, end: nightCluster.endSec });
  for (const c of napClusters) {
    events.push({ start: c.sessions[0]!.started_at, end: c.endSec });
  }
  if (nextCluster && nextCluster !== nightCluster && !napClusters.includes(nextCluster)) {
    events.push({ start: nextCluster.sessions[0]!.started_at, end: nextCluster.sessions[0]!.started_at });
  }

  const out: number[] = [];
  for (let i = 0; i < events.length - 1; i++) {
    const w = Math.floor((events[i + 1]!.start - events[i]!.end) / 60);
    if (w > 0) out.push(w);
  }
  return out;
}

export type DayBucket = {
  date: Date;
  sleepNightMin: number;
  sleepDayMin: number;
  feedCount: number;
  pumpYieldMl: number;
  diaperCount: number;
};

export function computeWeekBuckets(opts: {
  sleep: SleepSession[];
  feed: FeedingSession[];
  pump: PumpingSession[];
  diaper: DiaperEvent[];
  end: Date; // last day inclusive
  weekStart?: 'monday' | 'sunday';
  now?: Date;
}): DayBucket[] {
  const { sleep, feed, pump, diaper, end, weekStart = 'monday', now } = opts;
  const buckets: DayBucket[] = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(end);
    d.setDate(d.getDate() - i);
    const stats = computeDayStats({ sleep, feed, pump, diaper, date: d, now });
    buckets.push({
      date: startOfDayLocal(d),
      sleepNightMin: stats.lastNightMin,
      sleepDayMin: stats.napsMin,
      feedCount: stats.feedCount,
      pumpYieldMl: stats.pumpYieldMl,
      diaperCount: stats.diaperCount,
    });
  }
  if (weekStart === 'monday') {
    while (buckets[0]?.date.getDay() !== 1) {
      const first = buckets.shift();
      if (!first) break;
      buckets.push(first);
    }
  }
  return buckets;
}

export type HeatmapCell = { hour: number; day: number; asleepFraction: number };

export function computeHeatmap(opts: {
  sleep: SleepSession[];
  days: number;
  endDate: Date;
}): { rows: number[][] } {
  const { sleep, days, endDate } = opts;
  const rows: number[][] = Array.from({ length: days }, () => Array(24).fill(0));
  for (let d = 0; d < days; d++) {
    const date = new Date(endDate);
    date.setDate(date.getDate() - (days - 1 - d));
    const from = startOfDaySec(date);
    const to = from + DAY;
    for (const s of sleep) {
      const start = Math.max(s.started_at, from);
      const end = Math.min(s.ended_at ?? Math.floor(Date.now() / 1000), to);
      if (end <= start) continue;
      for (let h = 0; h < 24; h++) {
        const hourStart = from + h * 3600;
        const hourEnd = hourStart + 3600;
        const overlap = Math.max(0, Math.min(hourEnd, end) - Math.max(hourStart, start));
        if (overlap > 0) rows[d][h] += overlap / 3600;
      }
    }
  }
  return { rows };
}

/** Per-day sleep total via cluster attribution. Replaces the old midnight-cutoff
 *  bucketing used by Month trend. */
export function computeDailySleepTotals(opts: {
  sleep: SleepSession[];
  days: number;
  endDate: Date;
  now?: Date;
}): number[] {
  const { sleep, days, endDate, now } = opts;
  const clusters = clusterSleepSessions(sleep, now);
  const out: number[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(endDate);
    date.setDate(date.getDate() - i);
    const dayStart = startOfDaySec(date);
    let mins = 0;
    for (const c of clusters) if (c.attributedDayStartSec === dayStart) mins += c.totalMinutes;
    out.push(mins);
  }
  return out;
}
